import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  translations,
} from './translations';

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  supportedLanguages: SUPPORTED_LANGUAGES,
});

const normalizeLanguage = (value) => {
  const raw = String(value || '').toLowerCase().trim();
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return DEFAULT_LANGUAGE;
};

export const getInitialLanguage = () => {
  if (typeof window !== 'undefined') {
    const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved) return normalizeLanguage(saved);

    const browserLanguage = window.navigator?.language || DEFAULT_LANGUAGE;
    return normalizeLanguage(browserLanguage);
  }

  return DEFAULT_LANGUAGE;
};

const resolveMessage = (language, key) => {
  const parts = String(key || '').split('.');
  let current = translations[language] || translations[DEFAULT_LANGUAGE];

  for (const part of parts) {
    current = current?.[part];
    if (current === undefined) break;
  }

  if (typeof current === 'string') return current;

  let fallback = translations[DEFAULT_LANGUAGE];
  for (const part of parts) {
    fallback = fallback?.[part];
    if (fallback === undefined) break;
  }

  return typeof fallback === 'string' ? fallback : key;
};

const interpolate = (template, values) => {
  if (!values || typeof template !== 'string') return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    return values[token] !== undefined ? String(values[token]) : '';
  });
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    }

    setLanguageState(normalized);
  }, []);

  const t = useCallback(
    (key, values) => interpolate(resolveMessage(language, key), values),
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => useContext(I18nContext);

export const useI18n = () => useContext(I18nContext);
